/**
 * The app shell per the design reference v4: a left rail on desktop (Today · Look up · Book · Planning ·
 * People · Volunteers · Events, Book filled), a bottom tab bar on phones (Today · Look up · Book ·
 * Planning · More), a two-row phone header (mark + "ABF Scheduler" + gear, then the program strip), the
 * gear opening its menu (src/app/GearMenu.tsx). The crew sees Today only. 1.5.22: Admin is under the gear
 * only (Admin portal) — not in the rail, not in More. 1.6.2: More lists Help & Support too.
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Role } from '@shared/roles';
import { Icon } from '@/components/Icons';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useBooking } from '@/features/booking/BookingProvider';
import type { Me } from '@/auth/useMe';
import { replayWelcome } from '@/components/Welcome';
import { AskButton, useAssistantVisible } from '@/components/Assistant';
import { hasAnyRole, ACCESS } from '@shared/roles';
import { HELP_LINK, PROGRAM_PATH, ROUTE_ROLES, legacyHomeRedirect, moreFor, navFor, programOfPath, tabsFor, type NavItem } from './nav';
import { GearMenu } from './GearMenu';
import { useProgram } from './program';
import { ProgramNav } from './ProgramNav';
import { ConnectionLost } from './ConnectionLost';
import { UpdatingNote } from './UpdatingNote';
import { useMessagesWaiting } from './useMessagesWaiting';
import { useRouteTitle } from './useRouteTitle';
import { HouseTodayPage } from '@/pages/house/HouseToday';
import { PrintFrame } from '@/components/PrintPage';

function initials(name: string): string {
  return name.slice(0, 2).toUpperCase();
}

/**
 * Housekeeping's head row (1.8.4, D-1.8.4-4): Day · Planning · Schedule — its day (`/`, the screen Today was), and its
 * own pages, each only for the roles that open it. Housekeeping in numbers stays Planning's own link.
 */
function HousekeepingRow({ role }: { role: Role }) {
  const { t } = useTranslation();
  const location = useLocation();
  const links = [
    { key: 'day', to: PROGRAM_PATH.housekeeping, label: t('nav184.hk.day') },
    { key: 'planning', to: '/planning', label: t('nav.planning') },
    { key: 'schedule', to: '/schedule', label: t('nav184.hk.schedule') },
  ].filter((l) => ROUTE_ROLES[l.to]!.includes(role));
  const onPlanning = location.pathname.startsWith('/planning');
  return (
    <div className="hk184-head no-print" data-testid="hk-row">
      <nav className="hk184-row" aria-label={t('nav184.hk.label')}>
        {links.map((l) => (
          <NavLink key={l.key} to={l.to} end className={({ isActive }) => `hk184-link${isActive || (l.key === 'planning' && onPlanning) || (l.key === 'day' && location.pathname === '/') ? ' on' : ''}`} aria-current={(l.key === 'planning' ? onPlanning : l.key === 'day' ? location.pathname === '/' || location.pathname === l.to : location.pathname === l.to) ? 'page' : undefined} data-testid={`hk-row-${l.key}`}>
            {l.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/** "/" anywhere (outside a text field) puts the cursor in Look up's box — on Look up, or opening it (1.8.4: one search). */
function useSlashShortcut(enabled: boolean) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      const page = document.getElementById('lookup-search') as HTMLInputElement | null;
      if (page && page.offsetParent !== null) page.focus();
      else navigate('/lookup', { state: { focusSearch: true } });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled, navigate]);
}

function RailItem({ item, onBook, waiting }: { item: NavItem; onBook: (el: HTMLElement) => void; waiting: number }) {
  const { t } = useTranslation();
  if (item.action === 'book') {
    return (
      <button className="navbtn primary" type="button" onClick={(e) => onBook(e.currentTarget)} aria-label={t('nav.bookAria')} data-showme="book">
        <Icon name={item.icon} />
        <span>{t('nav.book')}</span>
      </button>
    );
  }
  if (item.key === 'messages') {
    // 25 Oct 2026: read as "Messages, 3 waiting"; the number is also drawn
    return (
      <NavLink className="navbtn" to={item.to!} aria-label={waiting > 0 ? t('nav1025.messagesWaiting', { count: waiting }) : undefined} data-testid="nav-messages">
        <Icon name={item.icon} />
        <span>{t('nav.messages')}</span>
        {waiting > 0 ? (
          <span className="navcount" aria-hidden="true">
            {waiting}
          </span>
        ) : null}
      </NavLink>
    );
  }
  return (
    <NavLink className="navbtn" to={item.to!} end={item.to === '/'}>
      <Icon name={item.icon} />
      <span>{t(`nav.${item.key}`)}</span>
    </NavLink>
  );
}

export function AppShell({ me }: { me: Me }) {
  const askVisible = useAssistantVisible(me);
  const { t } = useTranslation();
  const role = me.role!;
  const waiting = useMessagesWaiting(role);
  const items = navFor(role);
  const [moreOpen, setMoreOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const booking = useBooking();
  const openBook = (el: HTMLElement) => booking.openBook({}, el);
  const { setProgram, hasToday } = useProgram();
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement>(null);
  const tabs = tabsFor(role);
  const more = moreFor(role);
  const isCrew = role === 'crew';
  // 1.8.4: More is always there for a role with tools — it holds what does not fit and Help & Support (the crew's
  // and the tech's bar stays Today alone, as before)
  const hasMore = more.length > 0 || role !== 'crew';
  const tabCount = tabs.length + (hasMore ? 1 : 0);
  const isTech = role === 'tech';
  // 26 Oct 2026 (Eli): `/` is always Today for the office (the administrator and co-administrator); the Housekeeping Day
  // lives only at `/housekeeping`. The scheduler and the viewer are sent there from `/`; the crew and the tech keep `/` as
  // their own home (their Today). The old forms of `/` (`?day=`, `?closeout=`, `?book=`, `?tab=`) move to where they belong.
  const path = location.pathname;
  const onHome = path === '/';
  const pathProgram = programOfPath(path);
  const hkPage = pathProgram === 'housekeeping';
  const showHouseToday = onHome && hasToday && !isCrew && !isTech;
  const legacy = onHome ? legacyHomeRedirect(role, location.search) : null;
  const showHkRow = !isCrew && !isTech && hkPage;
  useRouteTitle({ hasToday, role });
  // the address names the program: the strip follows it (Back and typed addresses included)
  useEffect(() => {
    if (pathProgram && !isCrew && !isTech) setProgram(pathProgram);
  }, [pathProgram, isCrew, isTech, setProgram]);
  const canSearch = hasAnyRole(me.roles, ACCESS.schedule) && !isCrew;
  useSlashShortcut(canSearch);

  // Move focus to the page heading on navigation, so keyboard and screen-reader users land on the new page.
  const first = useRef(true);
  const navState = useRef<unknown>(null);
  navState.current = location.state;
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    // 1.8.4: "/" opened Look up — the cursor goes in its box (Look up's own autofocus), not on the heading
    if ((navState.current as { focusSearch?: boolean } | null)?.focusSearch) {
      document.getElementById('lookup-search')?.focus();
      return;
    }
    mainRef.current?.querySelector<HTMLElement>('h1')?.focus();
  }, [location.pathname]);

  return (
    <>
      <a className="skip" href="#main">
        {t('app.skipToMain')}
      </a>
      <div id="shell" data-role={role}>
        <nav className="rail" aria-label={t('nav.main')}>
          <button type="button" className="logo-btn mark" aria-label={t('app.replayWelcome')} onClick={(e) => replayWelcome(e.currentTarget)} data-testid="logo-replay">
            <img className="mark-light" src="/brand/mark-light.png" alt="" />
            <img className="mark-dark" src="/brand/mark-dark.png" alt="" />
          </button>
          {items.map((it) => (
            <RailItem key={it.key} item={it} onBook={openBook} waiting={waiting} />
          ))}
          <div className="spacer" />
          <div className="userchip">
            <div className="avatar" aria-hidden="true">
              {initials(me.name)}
            </div>
            <span>{t(`roles.${role}`)}</span>
          </div>
        </nav>

        <div className="content">
          <header className="topbar">
            <button type="button" className="brand-sm logo-btn" aria-label={t('app.replayWelcome')} onClick={(e) => replayWelcome(e.currentTarget)} data-testid="logo-replay-sm">
              <img className="mark-light" src="/brand/mark-light.png" alt="" />
              <img className="mark-dark" src="/brand/mark-dark.png" alt="" />
              <span aria-hidden="true">{t('app.name')}</span>
            </button>
            {/* the crew has one job: no program strip; 1.8.5: the tech's strip is Tech Support alone */}
            {isCrew ? null : <ProgramNav role={role} />}
            <div className="grow" />
            {/* 25 Oct 2026: Ask sits in the header, beside the gear (it used to float over the page) */}
            {askVisible ? <AskButton /> : null}
            <GearMenu me={me} />
          </header>
          <ConnectionLost />

          {/* 1.7.5: on paper only — the letterhead over the page and the footer under it (print175.css) */}
          <PrintFrame printedBy={me.principal?.userDetails || me.name} />

          <main id="main" ref={mainRef} tabIndex={-1}>
            <UpdatingNote />
            {showHkRow ? <HousekeepingRow role={role} /> : null}
            {legacy ? <Navigate to={legacy} replace state={location.state} /> : showHouseToday ? <HouseTodayPage me={me} /> : <Outlet />}
          </main>

          <nav className={`tabbar cols-${tabCount}`} aria-label={t('nav.main')}>
            {tabs.map((it) =>
              it.action === 'book' ? (
                <button key={it.key} className="fab" type="button" onClick={(e) => openBook(e.currentTarget)} aria-label={t('nav.bookAria')} data-showme="book">
                  <Icon name={it.icon} />
                  <span>{t('nav.book')}</span>
                </button>
              ) : (
                <NavLink key={it.key} to={it.to!} end={it.to === '/'}>
                  <Icon name={it.icon} />
                  <span>{t(`nav.${it.key}`)}</span>
                </NavLink>
              ),
            )}
            {hasMore ? (
              <button
                type="button"
                onClick={(e) => {
                  opener.current = e.currentTarget;
                  setMoreOpen(true);
                }}
                aria-haspopup="dialog"
              >
                <Icon name="more" />
                <span>{t('nav.more')}</span>
              </button>
            ) : null}
          </nav>
        </div>
      </div>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent title={t('sheet.moreTitle')} returnFocusTo={opener}>
          {more.map((it) => (
            <button
              key={it.key}
              className="bigchip"
              type="button"
              onClick={() => {
                setMoreOpen(false);
                navigate(it.to!);
              }}
            >
              <Icon name={it.icon} width={24} height={24} />
              <span>{it.key === 'messages' && waiting > 0 ? t('nav1025.messagesWaiting', { count: waiting }) : t(`nav.${it.key}`)}</span>
            </button>
          ))}
          <button
            className="bigchip"
            type="button"
            data-testid="more-help"
            onClick={() => {
              setMoreOpen(false);
              navigate(HELP_LINK.to);
            }}
          >
            <Icon name={HELP_LINK.icon} width={24} height={24} />
            <span>{t(HELP_LINK.titleKey)}</span>
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
}
